import type { BoxProps } from '@hover/blueprint';
import { Box, Center, Icon, Image, transition } from '@hover/blueprint';
import { iChevronLeft, iGrid, iPlus, iconEdit } from '@hover/icons';
import { AnimatePresence } from 'framer-motion';
import type { RefObject } from 'react';
import React, { useMemo, useState } from 'react';
import { generatePath, useHistory } from 'react-router';

import { FAVORITES_PAGE_SIZE } from 'src/modules/homeownerApp/constants';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { Button, IconButton } from 'src/shared/components';
import type { AiDisplayImage, Maybe } from 'src/shared/graphql/generated';
import { useAiDisplayImagesQuery } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Route } from 'src/shared/route';

import { FullscreenImageThumbnail } from '../../FullscreenImage';
import { InfiniteCarousel } from '../InfiniteCarousel';
import { useCurrentProject } from '../Menu/EditProject';

const CAROUSEL_CONTROLS_BACKGROUND = 'darkAlpha.700';

interface ProjectCarouselProps extends BoxProps {
  addables: Maybe<AiDisplayImage[]> | undefined;
  projectNameVisibilityHandler?: RefObject<HTMLDivElement>;
}

export const getProjectImages = (
  assets: Maybe<AiDisplayImage[]> | undefined,
) => {
  const images: {
    generatedImage: AiDisplayImage;
    src: string;
    edited: boolean;
  }[] = [];

  assets?.forEach(asset => {
    images.push({
      generatedImage: asset,
      src:
        asset.aiGeneratedImage.generatedImagePath ||
        asset.aiGeneratedImage.imagePath,
      edited: !!asset.aiGeneratedImage.edits,
    });
  });

  return images;
};

const ProjectCarousel = ({
  projectNameVisibilityHandler,
  addables,
  ...rest
}: ProjectCarouselProps) => {
  const [gridButtonVisible, setGridButtonVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { setCurrentMenu } = useHomeownerAppContext();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const isMobile = useIsMobileSize();

  const images = useMemo(
    () => getProjectImages(addables).reverse() || [],
    [addables],
  );
  const history = useHistory();
  const { project } = useCurrentProject();

  const { data: displayImagesData, loading: isLoadingSavedDisplayImages } =
    useAiDisplayImagesQuery({
      variables: { favorite: true, perPage: FAVORITES_PAGE_SIZE },
    });

  const savedImages = useMemo(() => {
    const existingGeneratedImageIds = project?.addables?.map(
      ({ aiGeneratedImage }) => aiGeneratedImage.id,
    );
    const favoritedDisplayImages =
      displayImagesData?.aiDisplayImages?.results.filter(displayImage => {
        const relevantGeneratedImageId = displayImage?.aiGeneratedImage
          ?.id as number;
        return !existingGeneratedImageIds?.includes(relevantGeneratedImageId);
      }) || [];
    return favoritedDisplayImages;
  }, [project, displayImagesData]);

  const showAddFromSaved =
    !isLoadingSavedDisplayImages && savedImages.length > 0;

  const handleEditClick = (aiDisplayImageId: number) => {
    history.push(
      generatePath(Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path, {
        projectId: project!.id,
        aiDisplayImageId,
      }),
    );
  };

  return (
    <Box
      height={{ base: '460px', tablet: '100dvh' }}
      onMouseEnter={() => {
        setGridButtonVisible(true);
      }}
      onMouseLeave={() => {
        setGridButtonVisible(false);
      }}
      overflow="hidden"
      position={{ base: 'relative', tablet: 'sticky' }}
      top={0}
      {...rest}
    >
      <Box
        alignItems="center"
        display={{ base: 'none', tablet: 'flex' }}
        p={800}
        position="absolute"
        top={0}
        width="100%"
        zIndex={1}
      >
        <IconButton
          colorScheme="light"
          icon={iChevronLeft}
          label="back"
          onClick={() => {
            history.push(Route.HOMEOWNER_APP_PROJECTS_LIST.path);
          }}
        />
      </Box>
      <AnimatePresence initial={false}>
        <InfiniteCarousel
          controlsBackground={CAROUSEL_CONTROLS_BACKGROUND}
          key={images.length}
          numberOfElements={images.length}
        >
          {({ index }) => {
            const modulo = index % images.length;
            const imageIndex = modulo < 0 ? images.length + modulo : modulo;
            setCurrentIndex(imageIndex === 0 ? images.length : imageIndex);
            const { generatedImage, src, edited } = images?.[imageIndex] || {};
            const showLabel = !isMobile || edited;
            const label = edited
              ? t('projects.edit.edited')
              : t('projects.edit.edit');

            if (!src)
              return (
                <Box
                  backgroundColor="neutral.100"
                  cursor="auto"
                  height="100%"
                  width="100%"
                />
              );

            return (
              <FullscreenImageThumbnail image={generatedImage} width="100%">
                <Image
                  alt={t('projects.projectCarouselImageAlt')}
                  borderRadius={{ base: 400, tablet: 'unset' }}
                  data-testid="project-carousel-image"
                  draggable={false}
                  height="100%"
                  objectFit="cover"
                  onClick={() => handleEditClick(generatedImage.id)}
                  src={src}
                  width="100%"
                />
                {/* Image footer */}
                <Box
                  alignItems="center"
                  bottom={0}
                  justifyContent="space-between"
                  pb={{ base: 600, tablet: 800 }}
                  position="absolute"
                  px={{ base: 600, tablet: 800 }}
                  ref={projectNameVisibilityHandler}
                  width="100%"
                >
                  <Button
                    backgroundColor={CAROUSEL_CONTROLS_BACKGROUND}
                    label={label}
                    onClick={() => handleEditClick(generatedImage.id)}
                    px={300}
                    shape={isMobile && !edited ? 'circle' : 'pill'}
                    sx={{
                      '.button-label': {
                        display: 'flex',
                        gap: 200,
                      },
                    }}
                  >
                    {showLabel && label}
                    {!edited && <Icon icon={iconEdit} isInline />}
                  </Button>
                  <IconButton
                    backgroundColor={CAROUSEL_CONTROLS_BACKGROUND}
                    icon={iPlus}
                    label={t('projects.addIdeas.heading')}
                    onClick={() =>
                      setCurrentMenu(HomeownerAppMenuNames.ADD_IDEAS_TO_PROJECT)
                    }
                    opacity={showAddFromSaved ? 1 : 0}
                  />
                </Box>
              </FullscreenImageThumbnail>
            );
          }}
        </InfiniteCarousel>
      </AnimatePresence>
      {/* Grid button */}
      <Center
        {...transition.easing250('opacity')}
        backgroundColor="teal"
        bottom={1000}
        display={{ base: 'none', tablet: images.length > 1 ? 'flex' : 'none' }}
        left="50%"
        opacity={gridButtonVisible ? 1 : 0}
        position="absolute"
        right="50%"
      >
        <Button
          backgroundColor={CAROUSEL_CONTROLS_BACKGROUND}
          iconAfter={iGrid}
          label="grid-button"
          onClick={() => {
            setCurrentMenu(HomeownerAppMenuNames.SAVED_IMAGES);
          }}
        >
          {t('projects.gridButton', {
            index: currentIndex,
            total: images.length,
          })}
        </Button>
      </Center>
    </Box>
  );
};

export { ProjectCarousel };
